import { FC, JSX, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import Ellipsis from '@semcore/ui/ellipsis';

import { useUploadFileMutation } from '@/entities/content/api/contentApi';
import { StylesConfig } from '@/entities/content/api/types';
import { config } from '@/shared/lib';
import { cacheFileForUrl } from '@/shared/lib/serviceWorker';
import { toastError } from '@/shared/lib/toastManager';
import CheckmarkIcon from '@/shared/ui/icons/Checkmark';
import CloseIcon from '@/shared/ui/icons/Close';
import LogoText from '@/shared/ui/icons/LogoText';
import Input from '@/shared/ui/Input';

import Button from '../../../../shared/ui/button';
import DragAndDropZone from '../DragAndDropZone';
import { SwitchSetting } from '../Settings';

import PositionSelector from './PositionSelector';

interface ISettingsProps {}

const Settings: FC<ISettingsProps> = (): JSX.Element => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<{ config: StylesConfig }>();
  const [uploadFile] = useUploadFileMutation();

  const logoSettingsEnabled = watch('config.logo.enabled');
  const overVideoEnabled = watch('config.logo.over_video');
  const responseFilePath = watch('config.logo.file_path');
  const prevLogoEnabled = useRef(logoSettingsEnabled);

  // Local state for image preview during upload
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState(false);

  const logoSrc = useMemo(() => {
    if (logoFile) {
      return URL.createObjectURL(logoFile);
    }
    return responseFilePath
      ? `${config.MEDIA_CONTENT_URL}${responseFilePath}`
      : '';
  }, [logoFile, responseFilePath]);

  // Reset logo files when logo is disabled
  useEffect(() => {
    if (prevLogoEnabled.current && !logoSettingsEnabled) {
      setValue('config.logo.file_path', '');
      setLogoFile(null);
      setUploadError(false);
    }
    prevLogoEnabled.current = logoSettingsEnabled;
  }, [logoSettingsEnabled, setValue]);

  const fileSelectedHandler = async (file: File | null) => {
    setValue('config.logo.file_path', '');
    setLogoFile(file);
    setUploadError(false);

    if (!file) {
      return;
    }

    try {
      const response = await uploadFile({ file, type: 'logo' }).unwrap();
      if (response?.data) {
        const mediaUrl = `${config.MEDIA_CONTENT_URL}${response.data.path}`;

        // Cache the file in SW so it's available immediately (CDN takes ~30s)
        try {
          await cacheFileForUrl(mediaUrl, file);
        } catch (cacheError) {
          console.warn('Failed to cache file in service worker:', cacheError);
          // Continue even if caching fails - the local blob preview will work
        }

        setValue('config.logo.file_path', response.data.path);
        setLogoFile(null);
        setUploadError(false);
      }
    } catch (error) {
      setUploadError(true);
      toastError(
        'Failed to upload logo. Please try again.',
        'logoUploadFailed'
      );
    }
  };

  const resetLogoFiles = () => {
    setValue('config.logo.file_path', '');
    setLogoFile(null);
    setUploadError(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex">
        <SwitchSetting title="Logo" name="config.logo.enabled" type="group" />
      </div>
      <div
        className={twMerge(
          'flex flex-col gap-2',
          logoSettingsEnabled ? '' : 'hidden'
        )}
      >
        {logoSrc ? (
          <div className="flex flex-col gap-2">
            <div className="flex gap-3 rounded-lg border border-[#d6d6d6] p-3">
              <div
                className={`h-10 w-10 items-center rounded border border-[#f2f4f6] px-[9px] py-1.5 ${uploadError ? 'opacity-50' : ''}`}
              >
                <img src={logoSrc} alt="Logo preview" />
              </div>
              <div className="flex grow flex-col gap-0.5">
                <div className="text-base font-light">
                  <Ellipsis trim="end" maxLine={1} tooltip>
                    {logoFile?.name ?? 'Uploaded logo file'}
                  </Ellipsis>
                </div>
                <div className="flex gap-2">
                  <div className="flex grow items-center">
                    <div
                      className={`h-1 w-full rounded-lg ${uploadError ? 'bg-red' : 'bg-blue'}`}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs leading-4 text-black">
                      {uploadError ? 'Error' : '100%'}
                    </p>
                    {!uploadError && <CheckmarkIcon />}
                  </div>
                </div>
              </div>
              <Button
                variant="function"
                size="sm"
                className="rounded-lg p-1"
                title={<CloseIcon />}
                onClick={resetLogoFiles}
              />
            </div>

            <div className="flex flex-col gap-3 px-2">
              <SwitchSetting
                title="Over video"
                name="config.logo.over_video"
                type="inline"
              />

              {overVideoEnabled ? (
                <div className="flex gap-2">
                  <PositionSelector />
                  <div className="flex grow flex-col gap-2">
                    <Input
                      type="number"
                      placeholder="Size"
                      errorClassName="hidden"
                      className="border border-gray/[0.5] bg-white"
                      {...register('config.logo.size', {
                        valueAsNumber: true,
                      })}
                    />
                    <Input
                      type="number"
                      placeholder="Opacity"
                      errorClassName="hidden"
                      className="border border-gray/[0.5] bg-white"
                      {...register('config.logo.opacity', {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-light text-black">
                    The logo will be located next to the ad player title
                  </div>
                  <div className="flex gap-2">
                    <div className="flex w-1/2 flex-col gap-2">
                      <div className="text-xs font-medium text-black">
                        for Inpage
                      </div>
                      <div className="flex flex-col gap-1 rounded-lg bg-[#f6f8ff] p-2">
                        <div className="h-[84px] w-full rounded-lg bg-blue/[.08]" />
                        <div className="flex gap-1">
                          <div className="rounded bg-blue/[.08] px-1">
                            <LogoText className="block h-full w-[32px]" />
                          </div>
                          <div className="flex grow flex-col gap-0.5">
                            <div className="h-1 w-full bg-blue/[.08]" />
                            <div className="h-1 w-2/3 bg-blue/[.08]" />
                            <div className="h-1 w-1/2 bg-blue/[.08]" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex w-1/2 flex-col gap-2">
                      <div className="text-xs font-medium text-black">
                        for Slider
                      </div>
                      <div className="flex flex-col gap-1 rounded-lg bg-[#f6f8ff] p-2">
                        <div className="flex gap-1">
                          <div className="rounded bg-blue/[.08] px-1">
                            <LogoText className="block h-full w-[32px]" />
                          </div>
                          <div className="flex grow flex-col gap-0.5">
                            <div className="h-1 w-full bg-blue/[.08]" />
                            <div className="h-1 w-2/3 bg-blue/[.08]" />
                            <div className="h-1 w-1/2 bg-blue/[.08]" />
                          </div>
                        </div>
                        <div className="h-[84px] w-full rounded-lg bg-blue/[.08]" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <DragAndDropZone
              className="h-48"
              accept=".svg, .png"
              onFileSelected={fileSelectedHandler}
            />
            <div className="flex text-sm font-light text-red opacity-100 transition-all duration-300 ease-out">
              {errors?.config?.logo?.enabled?.message}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
